// Per-stop edits hook (D3g UI). Surfaces the D3g per-stop edit
// backend (server/mfg-services/stopsService.updateOrgStop) as two
// affordances on the route detail card:
//   - Quick actions: one-tap "Mark arrived" / "Mark departed" buttons
//     for the driver flow. Stamps arrived_at / departed_at with now().
//   - Admin edit form: full whitelist of editable fields for
//     corrections (recipient, address, window, arrival/departure
//     timestamps, evidence url + completed_at, notes).
//
// Sibling to useLogisticsRouteDetail + useLogisticsRouteStateTransitions
// (the latter was extracted in the D3g UI prep commit). The detail card
// composes all three. Splitting by affordance keeps each hook under the
// file-size soft-warning threshold and matches the manufacturing
// useManufacturingMaterials / useManufacturingReceiveLines pattern.
//
// Three-way patching: every nullable field on the wire uses the
// undefined (omit) / null (clear) / string (set) convention shared by
// updateLogisticsRouteState + updateLogisticsCarrier.
// diffNullable() computes the right wire value from (current, draft):
// only sends a field that actually changed, prefers null over "" so the
// backend explicitly clears rather than rejecting an empty string.
//
// Stop kind is NOT editable -- the backend ignores it on update and
// uses the stored stop_kind for the recipient-shape re-validation.
// The conditional recipient inputs in the form mirror the same
// predicates re-exported from useLogisticsRouteDetail.
import { useCallback, useEffect, useState } from "react";
import {
  updateLogisticsStop,
  uploadLogisticsStopEvidence,
  type LogiStop,
} from "@/lib/logistics";
import {
  stopKindAllowsContactRecipient,
  stopKindAllowsOrgRecipient,
  stopKindRequiresContactRecipient,
  stopKindRequiresOrgRecipient,
} from "./useLogisticsRouteDetail";
import {
  diffNullable,
  draftFromStop,
  readAddressField,
  type StopEditDraft,
} from "./stopEditDraftDiff";

// Re-export so the edit form's existing
// `import { useLogisticsRouteStopEdits } from "./useLogisticsRouteStopEdits"`
// hook-return typing still resolves StopEditDraft from this entry.
export type { StopEditDraft } from "./stopEditDraftDiff";

// "Which one-tap button on which stop is in flight" -- drives both the
// button-busy label and the disabled state on the sibling button.
// D3i added "evidence" for the file-upload quick-capture flow; it
// shares the same busy slot so a row can't fire arrived + evidence in
// the same tap (and the evidence upload is long-running -- network
// round-trip + actual file bytes -- so blocking the rest of the row
// while it runs is the right UX).
export type StopQuickAction = "arrived" | "departed" | "evidence";

export function useLogisticsRouteStopEdits(
  routeId: string | null,
  stops: LogiStop[],
  reload: () => Promise<void>
) {
  const [quickActionBusy, setQuickActionBusy] = useState<{
    stopId: string;
    action: StopQuickAction;
  } | null>(null);
  const [quickActionError, setQuickActionError] = useState<string | null>(null);
  const [editingStopId, setEditingStopId] = useState<string | null>(null);
  const [edit, setEdit] = useState<StopEditDraft | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Reset on route change so stale state from a previous route
  // doesn't bleed into the newly-selected one.
  useEffect(() => {
    setQuickActionBusy(null);
    setQuickActionError(null);
    setEditingStopId(null);
    setEdit(null);
    setEditError(null);
  }, [routeId]);

  const startEdit = useCallback((stop: LogiStop) => {
    setEditingStopId(stop.id);
    setEdit(draftFromStop(stop));
    setEditError(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingStopId(null);
    setEdit(null);
    setEditError(null);
  }, []);

  const submitMarkArrived = useCallback(
    async (stop: LogiStop) => {
      if (stop.arrived_at) return;
      setQuickActionBusy({ stopId: stop.id, action: "arrived" });
      setQuickActionError(null);
      const res = await updateLogisticsStop({
        stop_id: stop.id,
        arrived_at: new Date().toISOString(),
      });
      setQuickActionBusy(null);
      if (!res.ok) {
        setQuickActionError(res.error);
        return;
      }
      await reload();
    },
    [reload]
  );

  const submitMarkDeparted = useCallback(
    async (stop: LogiStop) => {
      if (stop.departed_at) return;
      // Backend allows departed without arrived (for admin
      // corrections), but the COMMON driver flow is arrived -> departed.
      // Gate the quick button to require arrival first; admins can
      // still bypass via the Edit form.
      if (!stop.arrived_at) {
        setQuickActionError(
          "Mark arrived first, or use Edit to set arrival + departure together."
        );
        return;
      }
      setQuickActionBusy({ stopId: stop.id, action: "departed" });
      setQuickActionError(null);
      const res = await updateLogisticsStop({
        stop_id: stop.id,
        departed_at: new Date().toISOString(),
      });
      setQuickActionBusy(null);
      if (!res.ok) {
        setQuickActionError(res.error);
        return;
      }
      await reload();
    },
    [reload]
  );

  // D3i evidence quick-capture. Fires the sign -> upload -> commit
  // flow. The commit response carries the updated stop (with the new
  // evidence_url + evidence_completed_at), but we still call reload()
  // because the parent useLogisticsRouteDetail owns the stops slice
  // we render against; the cheapest way to propagate the change is
  // its existing detail refetch.
  const submitCaptureEvidence = useCallback(
    async (stop: LogiStop, file: File) => {
      setQuickActionBusy({ stopId: stop.id, action: "evidence" });
      setQuickActionError(null);
      const res = await uploadLogisticsStopEvidence(stop.id, file);
      setQuickActionBusy(null);
      if (!res.ok) {
        setQuickActionError(res.error);
        return;
      }
      await reload();
    },
    [reload]
  );

  const submitEdit = useCallback(async () => {
    if (!editingStopId || !edit) return;
    const stop = stops.find((s) => s.id === editingStopId);
    if (!stop) {
      setEditError("Stop no longer in the route. Cancel and reopen.");
      return;
    }

    // Mirror only the "required" recipient checks for short-circuit
    // messaging; the "disallowed" checks are skipped because the
    // conditional UI hides those inputs entirely. Backend re-validates
    // either way.
    const orgIdTrim = edit.recipient_org_id_text.trim();
    const contactIdTrim = edit.recipient_contact_id_text.trim();
    if (stopKindRequiresOrgRecipient(stop.stop_kind) && !orgIdTrim) {
      setEditError("dealer_holding stops require a recipient_org_id.");
      return;
    }
    if (stopKindRequiresContactRecipient(stop.stop_kind) && !contactIdTrim) {
      setEditError("end_customer stops require a recipient_contact_id.");
      return;
    }

    // Address is stored as JSONB and the backend treats the field as a
    // full REPLACEMENT (not a merge), so we only send the address key
    // when at least one of the four inputs has changed. Empty draft +
    // any change = send null to clear; otherwise pack the non-empty
    // fields and send the object.
    const draftAddr = {
      street: edit.address_street.trim(),
      city: edit.address_city.trim(),
      state: edit.address_state.trim(),
      postal_code: edit.address_postal_code.trim(),
    };
    const storedAddr = {
      street: readAddressField(stop.address, "street"),
      city: readAddressField(stop.address, "city"),
      state: readAddressField(stop.address, "state"),
      postal_code: readAddressField(stop.address, "postal_code"),
    };
    const addressChanged =
      draftAddr.street !== storedAddr.street ||
      draftAddr.city !== storedAddr.city ||
      draftAddr.state !== storedAddr.state ||
      draftAddr.postal_code !== storedAddr.postal_code;
    const packedAddr: Record<string, unknown> = {};
    if (draftAddr.street) packedAddr.street = draftAddr.street;
    if (draftAddr.city) packedAddr.city = draftAddr.city;
    if (draftAddr.state) packedAddr.state = draftAddr.state;
    if (draftAddr.postal_code) packedAddr.postal_code = draftAddr.postal_code;

    setEditBusy(true);
    setEditError(null);
    const res = await updateLogisticsStop({
      stop_id: editingStopId,
      // Recipient fields are gated by stop_kind: the hidden input
      // case omits the field entirely so the backend doesn't get a
      // stale value from a previous edit.
      recipient_org_id: stopKindAllowsOrgRecipient(stop.stop_kind)
        ? diffNullable(stop.recipient_org_id, edit.recipient_org_id_text)
        : undefined,
      recipient_contact_id: stopKindAllowsContactRecipient(stop.stop_kind)
        ? diffNullable(stop.recipient_contact_id, edit.recipient_contact_id_text)
        : undefined,
      address: addressChanged
        ? Object.keys(packedAddr).length > 0
          ? packedAddr
          : null
        : undefined,
      scheduled_window_start: diffNullable(
        stop.scheduled_window_start,
        edit.scheduled_window_start_text
      ),
      scheduled_window_end: diffNullable(
        stop.scheduled_window_end,
        edit.scheduled_window_end_text
      ),
      arrived_at: diffNullable(stop.arrived_at, edit.arrived_at_text),
      departed_at: diffNullable(stop.departed_at, edit.departed_at_text),
      evidence_url: diffNullable(stop.evidence_url, edit.evidence_url_text),
      evidence_completed_at: diffNullable(
        stop.evidence_completed_at,
        edit.evidence_completed_at_text
      ),
      // notes is non-nullable on the wire; send the literal new string
      // when it differs from the stored value, omit otherwise.
      notes: edit.notes_text === stop.notes ? undefined : edit.notes_text,
    });
    setEditBusy(false);
    if (!res.ok) {
      setEditError(res.error);
      return;
    }
    setEditingStopId(null);
    setEdit(null);
    await reload();
  }, [editingStopId, edit, stops, reload]);

  return {
    quickActionBusy,
    quickActionError,
    editingStopId,
    edit,
    setEdit,
    editBusy,
    editError,
    submitMarkArrived,
    submitMarkDeparted,
    submitCaptureEvidence,
    startEdit,
    cancelEdit,
    submitEdit,
  };
}
